// src/pages/info-destacada/InfoDestacada.js
import React, { useEffect, useState } from 'react'
import Container from 'react-bootstrap/Container'
import Row from 'react-bootstrap/Row'
import Col from 'react-bootstrap/Col'
import Form from 'react-bootstrap/Form'
import Button from 'react-bootstrap/Button'
import Table from 'react-bootstrap/Table'
import Badge from 'react-bootstrap/Badge'
import Card from 'react-bootstrap/Card'
import Dropdown from 'react-bootstrap/Dropdown'
import DropdownButton from 'react-bootstrap/DropdownButton'
import Spinner from 'react-bootstrap/Spinner'
import { toast } from 'react-toastify'
import api from 'api/api'

import TopHeader from 'components/TopHeader'
import ColorPicker from 'components/ColorPicker'
import { getIconClass } from 'components/IconSelector'

import NuevaInfo from './NuevaInfo'
import EditarInfo from './EditarInfo'
import EliminarInfo from './EliminarInfo'

import './info-destacada.css'

function InfoDestacada() {
	const [loading, setLoading] = useState(false)
	const [guardandoConfig, setGuardandoConfig] = useState(false)
	const [config, setConfig] = useState({ maxItems: 5, colorFondo: '#6c757d' })
	const [items, setItems] = useState([])

	const [accionActual, setAccionActual] = useState({
		item: null,
		accion: ''
	})

	// Estado para drag & drop
	const [draggedItem, setDraggedItem] = useState(null)
	const [dropTarget, setDropTarget] = useState(null)

	// Cargar configuración
	const refreshConfig = async () => {
		try {
			const { datos } = await api.get('a/config-info-destacada')
			setConfig(datos)
		} catch (error) {
			toast.error('Error al cargar configuración')
			console.error(error)
		}
	}

	// Normalizar campo activo (puede venir como 1, '1', true, 0, '0', false)
	const normalizarActivo = (valor) => {
		return valor === 1 || valor === '1' || valor === true
	}

	// Cargar items
	const refreshItems = async () => {
		try {
			setLoading(true)
			const { datos } = await api.get('a/info-destacada')
			// Normalizar el campo 'activo' de cada item
			const itemsNormalizados = (datos || []).map(item => ({
				...item,
				activo: normalizarActivo(item.activo)
			}))
			setItems(itemsNormalizados)
		} catch (error) {
			toast.error('Error al cargar información destacada')
			console.error(error)
		} finally {
			setLoading(false)
		}
	}

	// Guardar configuración
	const guardarConfig = async () => {
		try {
			setGuardandoConfig(true)
			const { msg } = await api.put('a/config-info-destacada', config)
			toast.success(msg || 'Configuración guardada')
		} catch (error) {
			toast.error('Error al guardar configuración')
			console.error(error)
		} finally {
			setGuardandoConfig(false)
		}
	}

	// Toggle estado activo/inactivo
	const toggleEstado = async (id, nuevoEstado) => {
		try {
			const { msg } = await api.put(`a/info-destacada/${id}`, { activo: nuevoEstado })
			refreshItems()
			toast.success(msg || 'Estado actualizado')
		} catch (error) {
			toast.error('Error al actualizar estado')
			console.error(error)
		}
	}

	// CRUD operations
	const nuevaInfo = async (data) => {
		try {
			const { msg } = await api.post('a/info-destacada', data)
			refreshItems()
			cerrarAccion()
			toast.success(msg || 'Información creada correctamente')
		} catch (error) {
			console.error(error)
			toast.error('Error al crear la información')
		}
	}

	const editarInfo = async (data) => {
		try {
			const { msg } = await api.put(`a/info-destacada/${accionActual.item.id}`, data)
			refreshItems()
			cerrarAccion()
			toast.success(msg || 'Información actualizada correctamente')
		} catch (error) {
			console.error(error)
			toast.error('Error al actualizar la información')
		}
	}

	const eliminarInfo = async (id) => {
		try {
			const { msg } = await api.delete(`a/info-destacada/${id}`)
			refreshItems()
			cerrarAccion()
			toast.success(msg || 'Información eliminada correctamente')
		} catch (error) {
			console.error(error)
			toast.error('Error al eliminar la información')
		}
	}

	// Cambiar orden (drag & drop)
	const cambiarOrden = async (itemId, nuevoOrden) => {
		try {
			await api.put(`a/info-destacada/${itemId}/orden`, { orden: nuevoOrden })
			refreshItems()
		} catch (error) {
			toast.error('Error al actualizar el orden')
			console.error(error)
		}
	}

	// Handlers para drag & drop
	const handleDragStart = (e, item) => {
		setDraggedItem(item)
		e.dataTransfer.effectAllowed = 'move'
	}

	const handleDragOver = (e, item) => {
		e.preventDefault()
		e.dataTransfer.dropEffect = 'move'

		if (draggedItem && draggedItem.id !== item.id) {
			setDropTarget(item.id)
		}
	}

	const handleDragLeave = (e) => {
		setDropTarget(null)
	}

	const handleDrop = async (e, targetItem) => {
		e.preventDefault()

		if (!draggedItem || draggedItem.id === targetItem.id) {
			setDraggedItem(null)
			setDropTarget(null)
			return
		}

		await cambiarOrden(draggedItem.id, targetItem.orden)

		setDraggedItem(null)
		setDropTarget(null)
	}

	const handleDragEnd = () => {
		setDraggedItem(null)
		setDropTarget(null)
	}

	useEffect(() => {
		refreshConfig()
		refreshItems()
	}, [])

	const cerrarAccion = () => {
		setAccionActual({ item: null, accion: '' })
	}

	const handleConfigChange = (key, value) => {
		setConfig(prev => ({ ...prev, [key]: value }))
	}

	const puedeAgregar = items.length < config.maxItems

	// render
	return (
		<Container fluid id="info-destacada" className="vh-100">
			<TopHeader
				iconName="star"
				text="Información Destacada"
			/>

			{/* Panel de configuración */}
			<Card className="mb-4">
				<Card.Header>
					<i className="fa fa-cog me-2"></i>
					Configuración
				</Card.Header>
				<Card.Body>
					<Row className="align-items-end">
						<Col md={3}>
							<Form.Group className="mb-3 mb-md-0">
								<Form.Label>Máximo de elementos</Form.Label>
								<Form.Control
									type="number"
									min={1}
									max={10}
									value={config.maxItems}
									onChange={(e) => handleConfigChange('maxItems', parseInt(e.target.value) || 1)}
								/>
							</Form.Group>
						</Col>
						<Col md={4}>
							<ColorPicker
								label="Color de fondo"
								value={config.colorFondo}
								onChange={(color) => handleConfigChange('colorFondo', color)}
							/>
						</Col>
						<Col md={3}>
							{/* Vista previa del color */}
							<Form.Label>Vista previa</Form.Label>
							<div
								className="config-preview"
								style={{ backgroundColor: config.colorFondo }}
							>
								<i className="fas fa-truck" style={{ color: '#ffffff' }}></i>
								<span style={{ color: '#ffffff' }}>Texto de ejemplo</span>
							</div>
						</Col>
						<Col md={2} className="text-end">
							<Button
								variant="primary"
								onClick={guardarConfig}
								disabled={guardandoConfig}
							>
								{guardandoConfig ? (
									<><Spinner size="sm" className="me-2" /> Guardando...</>
								) : (
									<><i className="fa fa-save me-2"></i> Guardar</>
								)}
							</Button>
						</Col>
					</Row>
				</Card.Body>
			</Card>

			{/* Botón Nueva info */}
			<Row className="mb-3">
				<Col>
					<Button
						variant="primary"
						onClick={() => setAccionActual({ item: null, accion: 'nueva' })}
						disabled={!puedeAgregar}
					>
						<i className="fa fa-plus me-2"></i>
						Nueva información
					</Button>
					{!puedeAgregar && (
						<span className="ms-3 text-muted">
							<i className="fa fa-info-circle me-1"></i>
							Límite de {config.maxItems} elementos alcanzado
						</span>
					)}
				</Col>
			</Row>

			{/* Tabla de items */}
			<Row>
				<Col>
					<div className="info-tabla">
						<small>
							Total: {loading ? (
								<Spinner animation="border" size="sm" className="ms-2" />
							) : (
								<strong>{items.length}</strong>
							)} de {config.maxItems}
						</small>

						<Table striped bordered hover responsive className="mt-2 tabla-info">
							<thead>
								<tr>
									<th className="drag-handle-header"></th>
									<th className="orden-header text-center">Orden</th>
									<th className="icono-header text-center">Ícono</th>
									<th className="titulo-header">Título</th>
									<th className="contenido-header">Contenido</th>
									<th className="color-header text-center">Color</th>
									<th className="estado-header text-center">Estado</th>
									<th className="acciones-header">Acciones</th>
								</tr>
							</thead>
							<tbody>
								{loading ? (
									<tr>
										<td colSpan="8" className="text-center py-4">
											<Spinner animation="border" variant="primary" />
										</td>
									</tr>
								) : (
									items.length > 0 ? items.map((item) => (
										<tr
											key={item.id}
											className={`item-row ${draggedItem?.id === item.id ? 'dragging' : ''} ${dropTarget === item.id ? 'drop-target' : ''}`}
											draggable={true}
											onDragStart={(e) => handleDragStart(e, item)}
											onDragOver={(e) => handleDragOver(e, item)}
											onDragLeave={handleDragLeave}
											onDrop={(e) => handleDrop(e, item)}
											onDragEnd={handleDragEnd}
										>
											<td className="drag-handle-cell">
												<i className="fa fa-grip-vertical drag-handle"></i>
											</td>
											<td className="orden-cell text-center">
												<Badge bg="secondary">{item.orden}</Badge>
											</td>
											<td className="icono-cell text-center">
												<i className={`${getIconClass(item.icono)} fa-lg`}></i>
											</td>
											<td className="titulo-cell">
												<div dangerouslySetInnerHTML={{ __html: item.titulo }} />
											</td>
											<td className="contenido-cell">
												<div dangerouslySetInnerHTML={{ __html: item.contenido }} />
											</td>
											<td className="color-cell text-center">
												<div
													className="color-muestra"
													style={{ backgroundColor: item.colorLetra }}
													title={item.colorLetra}
												/>
											</td>
											<td className="estado-cell text-center">
												<Form.Check
													type="switch"
													id={`switch-${item.id}`}
													checked={item.activo}
													onChange={(e) => toggleEstado(item.id, e.target.checked)}
													label={item.activo ? 'Activo' : 'Inactivo'}
												/>
											</td>
											<td className="acciones-cell">
												<DropdownButton
													as="div"
													title={<i className="fa fa-ellipsis-v"></i>}
													id={`dropdown-item-${item.id}`}
													size="sm"
													variant="outline-secondary"
												>
													<Dropdown.Item onClick={() => setAccionActual({ item, accion: 'editar' })}>
														<i className="fa fa-pencil me-2"></i>
														Editar
													</Dropdown.Item>
													<Dropdown.Divider />
													<Dropdown.Item
														onClick={() => setAccionActual({ item, accion: 'eliminar' })}
														className="text-danger"
													>
														<i className="fa fa-trash me-2"></i>
														Eliminar
													</Dropdown.Item>
												</DropdownButton>
											</td>
										</tr>
									)) : (
										<tr>
											<td colSpan="8" className="text-center py-4">
												No hay información destacada. Haz clic en "Nueva información" para agregar.
											</td>
										</tr>
									)
								)}
							</tbody>
						</Table>
					</div>
				</Col>
			</Row>

			{/* Componentes para acciones CRUD */}
			<NuevaInfo
				mostrar={accionActual.accion === 'nueva'}
				guardar={nuevaInfo}
				cancelar={cerrarAccion}
			/>

			<EditarInfo
				mostrar={accionActual.accion === 'editar'}
				itemOriginal={accionActual.item}
				guardar={editarInfo}
				cancelar={cerrarAccion}
			/>

			<EliminarInfo
				mostrar={accionActual.accion === 'eliminar'}
				item={accionActual.item}
				cancelar={cerrarAccion}
				eliminar={eliminarInfo}
			/>
		</Container>
	)
}

export default InfoDestacada
